import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Lock, AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';

export function LoginPage() {
  const { signIn, confirmMfa, isLoading: authLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [requireMfa, setRequireMfa] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signIn(username, password);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'MFA_REQUIRED') {
        setRequireMfa(true);
      } else {
        setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await confirmMfa(mfaCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid MFA code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
      </div>

      <main className="login-main" role="main">
        <div className="login-hero" aria-hidden="true">
          <div className="login-hero-content">
            <div className="login-brand">
              <div className="login-brand-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <div>
                <h1 className="login-brand-name">AWS Resource Monitor</h1>
                <p className="login-brand-tagline">Centralized multi-account monitoring</p>
              </div>
            </div>
            <p className="login-hero-text">
              Monitor resources, track costs, and manage alerts across all your AWS accounts from a single pane of glass.
            </p>
            <div className="login-features">
              <div className="login-feature">
                <span className="login-feature-icon">📊</span>
                <span>Real-time resource inventory</span>
              </div>
              <div className="login-feature">
                <span className="login-feature-icon">💰</span>
                <span>Cost tracking & forecasting</span>
              </div>
              <div className="login-feature">
                <span className="login-feature-icon">🔔</span>
                <span>Smart alerting & thresholds</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-card">
          <header className="login-card-header">
            <h2 className="login-card-title">Sign in to your account</h2>
            <p className="login-card-subtitle">Use your VEDNIQ credentials</p>
          </header>

          {error && (
            <div className="login-alert" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={requireMfa ? handleMfaSubmit : handleSubmit} className="login-form" noValidate>
            {!requireMfa && (
              <>
                <div className="form-group">
                  <label htmlFor="username" className="form-label">Email or Username</label>
                  <div className="form-input-wrapper">
                    <span className="form-input-icon" aria-hidden="true">
                      <Lock size={18} />
                    </span>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className="form-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your email or username"
                      autoComplete="username"
                      required
                      disabled={isLoading || authLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">Password</label>
                  <div className="form-input-wrapper">
                    <span className="form-input-icon" aria-hidden="true">
                      <Lock size={18} />
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className="form-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                      disabled={isLoading || authLoading}
                    />
                    <button
                      type="button"
                      className="form-input-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {requireMfa && (
              <div className="form-group">
                <label htmlFor="mfaCode" className="form-label">MFA Code</label>
                <div className="form-input-wrapper">
                  <span className="form-input-icon" aria-hidden="true">
                    <Lock size={18} />
                  </span>
                  <input
                    type="text"
                    id="mfaCode"
                    name="mfaCode"
                    className="form-input"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    autoComplete="one-time-code"
                    required
                    disabled={isLoading || authLoading}
                    maxLength={6}
                  />
                </div>
                <p className="form-hint">Enter the code from your authenticator app</p>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary login-submit"
              disabled={isLoading || authLoading}
            >
              {isLoading || authLoading ? (
                <>
                  <Loader2 size={18} className="spin" aria-hidden="true" />
                  <span>{requireMfa ? 'Verifying...' : 'Signing in...'}</span>
                </>
              ) : (
                requireMfa ? 'Verify Code' : 'Sign in'
              )}
            </button>
          </form>

          <footer className="login-card-footer">
            <p className="login-footer-text">
              <strong>VEDNIQ</strong> AWS Resource Monitor
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}